import { MedicalResult, MedicalTranscriptEvent } from '@aws-sdk/client-transcribe-streaming';
import moment from 'moment-timezone';
import { api } from 'careand-redux/api/ApiThunks';
import { store } from 'careand-redux/store';
import { getUnwrappedDispatch } from 'careand-redux/useAppDispatch';
import { AppointmentTranscription } from 'generated/api';
import {
  AppointmentTranscriptionFromAWS,
  AppointmentTranscriptionFromAWSWithVoicePartition,
  AppointmentTranscriptionWithPartialIndicators,
  AwsTranscribeService,
} from './AWSTranscribeService';
import Bugsnag from '@bugsnag/js';

class TranscriptionServiceCounter {
  private _count = 0;

  public inc() {
    this._count += 1;
    this.log();
  }
  public dec() {
    this._count -= 1;
    this.log();
  }
  public log() {
    console.log('[count] TranscriptionServices: ', this._count);
  }
}

const transcriptionServiceCounter = new TranscriptionServiceCounter();

//@ts-expect-error TODO find out how to type this or scrape it altogether
window.transcriptionServiceCounter = transcriptionServiceCounter;

type TelemedicineTranscriptionCallback = (
  text: string,
  event: MedicalTranscriptEvent | undefined,
  allResults: MedicalResult[],
  merged: AppointmentTranscriptionWithPartialIndicators,
) => void;

// how many milliseconds between transcription events
// before we consider the transcription to be idle
// (ie. in silence)
const REALTIME_TRANSCRIPTION_TIMEOUT = 2000;

export const TRANSCRIPTION_STATUS = {
  active: 'active',
  transcribing: 'transcribing',
  inactive: 'inactive',
  starting: 'starting',
  ending: 'ending',
  error: 'error',
} as const;

export type TRANSCRIPTION_STATUS = (typeof TRANSCRIPTION_STATUS)[keyof typeof TRANSCRIPTION_STATUS];

/**
 * Responsible for:
 * - starting a mic + portal transcription
 * - automatically backing up the transcription
 * - sending events (through callbacks)
 *    - on new transcription event
 *    - on realtimeTranscribingStatusChanged (whether has received event in last 2 seconds)
 */
export class TelemedicineTranscriptionService {
  private isTranscribing = false;
  private results: MedicalResult[] = [];
  private oldTranscription: AppointmentTranscription | undefined;
  private realTimeTranscribingTimer: NodeJS.Timeout | null = null;
  private lastTranscriptionEventDate?: number;

  constructor(
    private awsService: AwsTranscribeService,
    private appointmentId: string,
    private transcriptionCallback: TelemedicineTranscriptionCallback | undefined,
    private onTranscriptionStatusChanged?: (value: TRANSCRIPTION_STATUS) => void,
  ) {}

  async loadExistingTranscription() {
    try {
      this.oldTranscription = await this.getDispatch()(
        api.Appointments.getAppointmentTranscription({ id: this.appointmentId }),
      );
      // If there's existing transcription, trigger callback to show it immediately
      if (this.oldTranscription?.messages?.length && this.transcriptionCallback) {
        this.transcriptionCallback(
          '', // no new text
          undefined, // no new event
          [], // no new results
          this.getMergedTranscription(true, true, false), // show existing with partial indicators
        );
      }
    } catch (error: any) {
      console.error('Failed to load existing transcription:', error);
      Bugsnag.notify(error);
    }
  }

  private onTranscriptionReceived() {
    if (!this.onTranscriptionStatusChanged) {
      return;
    }
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.transcribing);
    if (this.realTimeTranscribingTimer !== null) {
      clearTimeout(this.realTimeTranscribingTimer);
    }
    this.realTimeTranscribingTimer = setTimeout(() => {
      if (!this.isTranscribing) {
        this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.inactive);
      }
      this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.active);
    }, REALTIME_TRANSCRIPTION_TIMEOUT);
  }

  async start() {
    if (this.isTranscribing) {
      throw { message: 'Already Transcribing' };
    }
    transcriptionServiceCounter.inc();
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.starting);

    this.awsService.transcriptionCallback = (text, event, ...args) => {
      const results = event?.Transcript?.Results;
      if (!results || results.length === 0) {
        return;
      }
      this.results = [...this.results, ...results];
      this.lastTranscriptionEventDate = moment().unix();
      const isVoicePartitioned = this.awsService.isVoicePartitioned();
      this.transcriptionCallback?.(
        text,
        event,
        this.results,
        //@ts-expect-error
        this.getMergedTranscription(true, !isVoicePartitioned, isVoicePartitioned),
      );
      this.onTranscriptionReceived();
    };

    await this.loadExistingTranscription();

    await this.awsService.start();

    this.isTranscribing = true;
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.transcribing);
  }

  private getMergedTranscription(
    withPartials: boolean,
    withPartialIndicators: true,
    isVoicePartitioned: boolean,
  ): AppointmentTranscriptionWithPartialIndicators;
  private getMergedTranscription(
    withPartials: boolean,
    withPartialIndicators: false,
    isVoicePartitioned: boolean,
  ): AppointmentTranscription;
  private getMergedTranscription(
    withPartials: boolean,
    withPartialIndicators: boolean,
    isVoicePartitioned: boolean,
  ) {
    let transcription;
    if (isVoicePartitioned) {
      transcription = AppointmentTranscriptionFromAWSWithVoicePartition(
        this.appointmentId,
        this.results,
      );
    } else {
      transcription = AppointmentTranscriptionFromAWS(
        this.appointmentId,
        this.results,
        withPartials,
        //@ts-expect-error
        withPartialIndicators,
      );
    }
    if (this.oldTranscription) {
      transcription.messages = [...this.oldTranscription.messages, ...transcription.messages];
    }
    return transcription;
  }

  async stop() {
    console.log('[debug] stopping...');
    if (!this.isTranscribing) {
      console.log('[debug] had stoppped already');
      return;
    }
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.ending);
    console.log('[debug] stopping... for realsies this time');
    transcriptionServiceCounter.dec();
    this.isTranscribing = false;
    if (this.realTimeTranscribingTimer) {
      console.log('[debug] stopping... clearing transcribing timer');
      clearTimeout(this.realTimeTranscribingTimer);
      this.realTimeTranscribingTimer = null;
    }
    try {
      console.log('[debug] stopping... awsService.stop');
      await this.awsService?.stop();
    } catch (err: any) {
      console.log(err);
      Bugsnag.notify(err);
    }
    console.log('[debug] stopping... dispatching state change to inactive');
    this.onTranscriptionStatusChanged?.(TRANSCRIPTION_STATUS.inactive);
  }

  private getDispatch() {
    return getUnwrappedDispatch(store.dispatch);
  }
}
